import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, Input, Label, FormFeedback } from "reactstrap";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import Icon from "Components/Icons/Icon";
import CustomButton from "Components/Custom/CustomButton";
import CustomInput from "Components/Custom/CustomInput";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  createInvoice,
  deleteAttachment,
  editInvoice,
  getQuoteById,
  getQuotes,
} from "slices/thunks";
import {
  addDaysToToday,
  formatDateToYMD,
  formatToDayMonYear,
} from "utils/formatDates";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { InvoiceDetailsPayload } from "types/invoice.api";
import {
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
} from "slices/invoices/reducer";
import AttachmentsList from "Components/Common/AttachmentsList";
import { useDebounce } from "Components/Hooks/useDebounce";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createOptionalDescriptionValidation,
} from "Components/constants/validation";
import { ErrorHandler, ErrorToast } from "utils/toast";
import { CustomQuoteOption, CustomSingleValue } from "./CustomQuoteOption";
import { compressImage } from "utils/compressImage";

export interface InvoiceFormValues {
  linkedQuote: {
    label: string;
    value: number;
  } | null;
  quote: string;
  client: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  project: string;
  notes: string;
  attachments: { name: string; uri: string; type: string }[] | null;
}

interface ManageInvoiceSummaryProps {
  template: string;
  categorised: string;
  isEdit?: boolean;
  onSubmit: () => void;
  invoiceData: InvoiceDetailsPayload | null;
  onDatesChange: (
    quoteDate?: string | undefined,
    expiryDate?: string | undefined,
  ) => void;
  quoteId?: number;
  reference_number?: string;
  isHidePhoneNumber?: boolean;
  handleHidePhoneNumberToggle?: () => void;
}

const ManageInvoiceSummary: React.FC<ManageInvoiceSummaryProps> = ({
  template,
  categorised,
  isEdit,
  onSubmit,
  invoiceData,
  onDatesChange,
  quoteId,
  reference_number,
  isHidePhoneNumber = false,
  handleHidePhoneNumberToggle = () => {},
}) => {
  document.title = "Manage Invoice | EaziQuote";
  const dispatch = useAppDispatch();
  const configData = useAppSelector((state) => state.config.configData);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const statusOfCreateInvoice = useAppSelector(
    (state) => state.invoice.status.add,
  );
  const statusOfEditInvoice = useAppSelector(
    (state) => state.invoice.status.edit,
  );

  // handle quote
  const quoteDetails = useAppSelector((state) => state.quote.quoteDetails);
  const [searchTerm, setSearchTerm] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const appQuote = useAppSelector((state) => state.quote);
  const quotes = appQuote.quotes;
  const isQuoteLoading = appQuote.status.fetch;

  const quoteOptions = quotes.map((quote) => ({
    label: quote.title,
    value: quote.id,
    referenceNumber: quote.reference_number,
  }));

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const validation: FormikProps<InvoiceFormValues> =
    useFormik<InvoiceFormValues>({
      enableReinitialize: true,

      initialValues: isEdit
        ? {
            linkedQuote: invoiceData
              ? {
                  label: invoiceData.quote.reference_number,
                  value: invoiceData.quote.id,
                }
              : null,
            quote: invoiceData?.quote.title ?? "",
            client: invoiceData?.client.name ?? "",
            invoiceNumber: invoiceData?.invoice_number ?? "IN",
            issueDate: invoiceData?.invoice_date ?? "",
            dueDate: invoiceData?.due_date ?? "",
            project: invoiceData?.message ?? "",
            notes: invoiceData?.notes ?? "",
            attachments: [],
          }
        : {
            linkedQuote: quoteDetails
              ? {
                  label: quoteDetails.reference_number,
                  value: quoteDetails.id,
                }
              : null,
            quote: quoteDetails?.title ?? "",
            client: quoteDetails?.client.name ?? "",
            invoiceNumber: "IN",
            issueDate: new Date().toISOString().split("T")[0],
            dueDate: addDaysToToday(
              configData?.billing_preferences.payment_expiration || 0,
            ),
            project: "",
            notes: "",
            attachments: [],
          },
      validationSchema: Yup.object({
        linkedQuote: Yup.object()
          .shape({
            label: Yup.string().required(),
            value: Yup.number().required(),
          })
          .nullable()
          .required("Please Select Linked Quote"),
        quote: Yup.string().required(VALIDATION_MESSAGES.REQUIRED("quote")),
        client: Yup.string().required(VALIDATION_MESSAGES.REQUIRED("client")),
        invoiceNumber: Yup.string().required(
          VALIDATION_MESSAGES.REQUIRED("invoice number"),
        ),
        issueDate: Yup.string().required(
          VALIDATION_MESSAGES.REQUIRED("issue date"),
        ),
        dueDate: Yup.string()
          .required(VALIDATION_MESSAGES.REQUIRED("due date"))
          .test(
            "is-after-issue-date",
            "Due date must be the same or after issue date",
            function (value) {
              const { issueDate } = this.parent;

              if (!issueDate || !value) return true; // Already handled by required()

              return new Date(value) >= new Date(issueDate);
            },
          ),
        notes: Yup.string()
          .nullable()
          .max(
            VALIDATION_LIMITS.NOTES.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Notes",
              VALIDATION_LIMITS.NOTES.MAX,
            ),
          )
          .test(
            "valid-notes",
            createOptionalDescriptionValidation("Notes").message,
            (value) =>
              createOptionalDescriptionValidation("Notes").test(
                value ?? undefined,
              ),
          ),
        attachments: Yup.array().max(
          5,
          "You can upload a maximum of 5 attachments",
        ),
        project: Yup.string()
          .nullable()
          .max(
            VALIDATION_LIMITS.DESCRIPTION.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Notes",
              VALIDATION_LIMITS.DESCRIPTION.MAX,
            ),
          )
          .test(
            "valid-project",
            createOptionalDescriptionValidation("Notes").message,
            (value) =>
              createOptionalDescriptionValidation("Notes").test(
                value ?? undefined,
              ),
          ),
      }),
      onSubmit: async (values, { resetForm }) => {
        console.log("values", values);
        try {
          if (isEdit && invoiceData?.id) {
            await dispatch(
              editInvoice({
                invoice_id: invoiceData.id,
                invoice_date: values.issueDate,
                due_date: values.dueDate,
                message: values.project?.trim(),
                notes: values.notes?.trim(),
                attachments: values.attachments || [],
                categorised,
                template,
                is_company_phone_number_show: isHidePhoneNumber ? 0 : 1,
              }),
            )?.unwrap();
            dispatch(resetEditInvoiceStatus());
          } else {
            await dispatch(
              createInvoice({
                quote_id: values.linkedQuote!.value,
                invoice_date: values.issueDate,
                due_date: values.dueDate,
                message: values.project?.trim(),
                notes: values.notes?.trim(),
                attachments: values.attachments || [],
                categorised,
                template,
                is_company_phone_number_show: isHidePhoneNumber ? 0 : 1,
              }),
            )?.unwrap();

            dispatch(resetCreateInvoiceStatus());
          }
          onSubmit?.();
          resetForm();
          setAttachments([]);
          setPreviewUrls([]);
        } catch (error) {
          ErrorHandler(error);
        }
      },
    });

  // Handle file selection with preview
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    // Validate file type - only allow PDF, JPEG, PNG, and GIF
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/gif",
      "application/pdf",
    ];
    const allowedExtensions = ["jpg", "jpeg", "png", "gif", "pdf"];

    // Check each file for valid type
    for (const file of files) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension || "")
      ) {
        e.target.value = ""; // Reset the input
        ErrorToast("Only PDF, JPEG, PNG, and GIF files are allowed");
        return;
      }
    }

    let existingCount = 0;

    if (quoteDetails?.attachments?.length && !isEdit) {
      existingCount = quoteDetails.attachments.length;
    } else if (invoiceData?.attachments?.length) {
      existingCount = invoiceData.attachments.length;
    }

    const newCount = attachments.length;
    const requestedCount = files.length;

    const totalCount = existingCount + newCount + requestedCount;

    if (totalCount > 5) {
      validation.setFieldTouched("attachments", true, false);
      validation.setFieldError(
        "attachments",
        "You can upload a maximum of 5 attachments",
      );

      e.target.value = ""; // reset input
      return;
    }

    try {
      // 🔥 Compress only images
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return await compressImage(file, 2); // 2MB limit
          }
          return file; // PDF untouched
        }),
      );

      const newPreviewUrls = processedFiles.map((file) =>
        URL.createObjectURL(file),
      );

      const updatedFiles = [...attachments, ...processedFiles];
      const updatedPreviewUrls = [...previewUrls, ...newPreviewUrls];

      setAttachments(updatedFiles);
      setPreviewUrls(updatedPreviewUrls);

      validation.setFieldValue("attachments", updatedFiles);
    } catch (error: any) {
      ErrorToast(error || "Image compression failed");
    }

    e.target.value = ""; // reset input safely
  };

  const handleDeleteAttachment = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    const newPreviewUrls = previewUrls.filter((_, i) => i !== index);

    // Revoke the deleted preview URL
    URL.revokeObjectURL(previewUrls[index]);

    setAttachments(newAttachments);
    setPreviewUrls(newPreviewUrls);
    validation.setFieldValue("attachments", newAttachments);
  };

  // useEffect(() => {
  //   console.log("statusOfCreateInvoice", statusOfCreateInvoice);
  //   if (statusOfCreateInvoice === "succeeded") {
  //     onSubmit();
  //     dispatch(resetCreateInvoiceStatus());
  //   }
  // }, [onSubmit, statusOfCreateInvoice, dispatch]);

  // useEffect(() => {
  //   console.log("statusOfEditInvoice", statusOfEditInvoice);
  //   if (statusOfEditInvoice === "succeeded") {
  //     onSubmit();
  //     dispatch(resetEditInvoiceStatus());
  //   }
  // }, [onSubmit, statusOfEditInvoice, dispatch]);

  useEffect(() => {
    if (quoteId && reference_number) {
      validation.setFieldValue("linkedQuote", {
        label: reference_number,
        value: quoteId,
      });
      dispatch(getQuoteById(quoteId));
    }
  }, [quoteId, reference_number]);

  useEffect(() => {
    dispatch(
      getQuotes({
        status: ["approved"],
        search: debouncedSearchTerm,
      }),
    );
  }, [debouncedSearchTerm, dispatch]);

  return (
    <Row className="mt-4">
      <Col>
        <form onSubmit={validation.handleSubmit}>
          <Row>
            <Col className="col-lg-6">
              <div className="mb-3">
                <Label className="font-primary fw-normal fs-14 text-black2D d-flex">
                  Linked Quote
                </Label>
                <Select
                  value={validation.values.linkedQuote}
                  onChange={(option: { label: string; value: number }) => {
                    console.log("option", option);
                    validation.setFieldValue("linkedQuote", {
                      label: option.label,
                      value: option.value,
                    });
                    dispatch(getQuoteById(option.value));
                  }}
                  onInputChange={(inputValue: any) => {
                    handleSearch(inputValue);
                  }}
                  options={quoteOptions}
                  isLoading={isQuoteLoading === "loading"}
                  isDisabled={isEdit}
                  classNamePrefix="react-select"
                  components={{
                    Option: CustomQuoteOption,
                    SingleValue: CustomSingleValue,
                  }}
                  styles={{
                    menuPortal: (base: any) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    menu: (base: any) => ({
                      ...base,
                      borderColor: base.borderColor,
                      backgroundColor: isEdit ? "#F5F6FB" : "#fff",
                      "&:hover": {
                        borderColor: base.borderColor,
                      },
                    }),
                  }}
                  menuPortalTarget={document.body}
                  placeholder="Search or select a quote"
                  className={`auth-form-dropdown-control`}
                  loadingMessage={() => "Searching quotes..."}
                  noOptionsMessage={({ inputValue }: { inputValue: any }) =>
                    inputValue
                      ? `No quotes found for "${inputValue}"`
                      : "No quotes available"
                  }
                  // classNamePrefix="react-select"
                  // isLoading={isQuoteLoading === "loading"}
                  // isDisabled={isEdit}
                  // styles={{
                  //   menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                  //   control: (base: any) => ({
                  //     ...base,
                  //     borderColor: base.borderColor,
                  //     backgroundColor: isEdit ? "#F5F6FB" : "#fff",
                  //     "&:hover": {
                  //       borderColor: base.borderColor,
                  //     },
                  //   }),
                  // }}
                />
                {validation.errors.client && validation.touched.client && (
                  <div className="text-danger fs-12">
                    {validation.errors.client as string}
                  </div>
                )}
              </div>
            </Col>
            <Col className="col-lg-6">
              <CustomInput
                label="Quote"
                name="quote"
                type="text"
                value={validation.values.quote}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.quote}
                error={validation.errors.quote}
                placeholder="Enter Quote"
                isDisabled={true}
              />
            </Col>
          </Row>
          <Row>
            <Col className="col-lg-12">
              <CustomInput
                label="Client"
                name="client"
                type="text"
                value={validation.values.client}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.client}
                error={validation.errors.client}
                placeholder="Enter Client"
                isDisabled={true}
              />
            </Col>
          </Row>
          <Row>
            <div className="d-flex gap-2 align-content-center mt-1">
              <p className="font-primary fs-14 text-black2D fw-normal">
                Hide your phone number
              </p>
              <div
                className={`toggle-switch ${isHidePhoneNumber ? "on" : "off"}`}
                onClick={handleHidePhoneNumberToggle}
              >
                <span
                  className={`toggle-label ${isHidePhoneNumber ? "on" : "off"}`}
                >
                  {isHidePhoneNumber ? "Yes" : "No"}
                </span>
                <div
                  className={`toggle-circle ${
                    isHidePhoneNumber ? "on" : "off"
                  }`}
                />
              </div>
            </div>
          </Row>
          <div className="divider my-4" />
          <Row>
            <Col className="col-lg-12">
              <CustomInput
                label="Invoice Number"
                name="invoiceNumber"
                type="text"
                value={validation.values.invoiceNumber}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.invoiceNumber}
                error={validation.errors.invoiceNumber}
                placeholder="Enter Invoice Number"
                isDisabled={true}
              />
            </Col>
          </Row>

          <Row>
            <Col className="col-lg-6">
              <Label
                htmlFor="last-apply-date-Input"
                className="font-primary fw-normal fs-14 text-black2D d-flex"
              >
                Issue Date
              </Label>

              <div className="d-flex flex-row align-items-center">
                <Flatpickr
                  className="form-control"
                  placeholder="Enter Issue Date"
                  value={validation.values.issueDate}
                  id="datepicker-publish-input"
                  options={{
                    maxDate: validation.values.dueDate
                      ? new Date(validation.values.dueDate)
                      : undefined,
                    dateFormat: "Y-m-d",
                    disableMobile: true,
                  }}
                  onChange={(date: any) => {
                    if (date[0]) {
                      const formattedDate = formatDateToYMD(date[0]);
                      validation.setFieldValue("issueDate", formattedDate);
                      const formattedDateChange = formatToDayMonYear(date[0]);
                      onDatesChange(formattedDateChange, undefined);
                    }
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 22,
                    cursor: "pointer",
                    pointerEvents: "none",
                  }}
                />
              </div>
              {validation.errors.issueDate && validation.touched.issueDate && (
                <div className="text-danger fs-12 mt-1">
                  {validation.errors.issueDate}
                </div>
              )}
            </Col>

            <Col className="col-lg-6">
              <Label
                htmlFor="close-date-Input"
                className="font-primary fw-normal fs-14 text-black2D d-flex"
              >
                Due Date
              </Label>
              <div className="d-flex flex-row align-items-center">
                <Flatpickr
                  className="form-control"
                  placeholder="Enter Due Date"
                  value={
                    validation.values.dueDate
                      ? new Date(validation.values.dueDate)
                      : undefined
                  }
                  id="datepicker-publish-input"
                  options={{
                    minDate: validation.values.issueDate
                      ? new Date(validation.values.issueDate)
                      : undefined,
                    dateFormat: "Y-m-d",
                    disableMobile: true,
                  }}
                  onChange={(date: any) => {
                    if (date[0]) {
                      const formattedDate = formatDateToYMD(date[0]);
                      validation.setFieldValue("dueDate", formattedDate);
                      const formattedDateChange = formatToDayMonYear(date[0]);
                      onDatesChange(undefined, formattedDateChange);
                    }
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 22,
                    cursor: "pointer",
                    pointerEvents: "none",
                  }}
                />
              </div>
              {validation.errors.dueDate && validation.touched.dueDate && (
                <div className="text-danger fs-12 mt-1">
                  {validation.errors.dueDate}
                </div>
              )}
            </Col>
          </Row>

          <Row className="mt-3">
            <Col className="col-lg-12">
              <CustomInput
                label="Project / Services Notes"
                name="project"
                type="textarea"
                value={validation.values.project}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.project}
                error={validation.errors.project}
                placeholder="Explain what the invoice covers"
              />
            </Col>
          </Row>
          <Row>
            <Col className="col-lg-12">
              <CustomInput
                label="Notes (Not visible on invoice)"
                name="notes"
                type="textarea"
                value={validation.values.notes}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                touched={validation.touched.notes}
                error={validation.errors.notes}
                placeholder="Add private notes"
              />
            </Col>
          </Row>

          <hr className="my-4" />
          <div className="mb-4">
            <Label
              className="font-primary fw-normal fs-14 text-black2D d-flex"
              htmlFor="project-thumbnail-img"
            >
              Attachments
            </Label>
            <Input
              type="file"
              name="attachments"
              accept="image/jpeg,image/png,image/gif,application/pdf"
              multiple
              onChange={handleFileChange}
              invalid={
                !!(
                  validation.errors.attachments &&
                  validation.touched.attachments
                )
              }
              value={""}
            />

            <FormFeedback>{validation.errors?.attachments}</FormFeedback>

            {attachments && attachments.length > 0 && (
              <div className="mt-3">
                <small className="text-muted fw-semibold">
                  {attachments.length} file(s) selected
                </small>
                <Row className="mt-2 g-3">
                  {attachments.map((file: File, index: number) => (
                    <Col xs={6} sm={4} md={3} key={index}>
                      <div className="card border position-relative">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute"
                          style={{
                            top: "5px",
                            right: "5px",
                            padding: "2px 6px",
                            zIndex: 10,
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={() => handleDeleteAttachment(index)}
                          title="Delete attachment"
                        >
                          ×
                        </button>

                        <img
                          src={previewUrls[index]}
                          alt={file.name}
                          className="card-img-top"
                          style={{
                            height: "150px",
                            objectFit: "cover",
                          }}
                        />

                        <div className="card-body p-2">
                          <p
                            className="card-text small mb-1 text-truncate"
                            title={file.name}
                          >
                            {file.name}
                          </p>
                          <small className="text-muted">
                            {(file.size / 1024).toFixed(2)} KB
                          </small>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}

            {!isEdit && quoteDetails?.attachments && (
              <AttachmentsList
                attachments={quoteDetails?.attachments || []}
                onDelete={(id: number) => setDeleteModal(id)}
              />
            )}

            {invoiceData?.attachments && (
              <AttachmentsList
                attachments={invoiceData?.attachments || []}
                onDelete={(id: number) => setDeleteModal(id)}
              />
            )}
          </div>
          <div className="text-start mb-3 ">
            <CustomButton
              className="border-7 bg-primary w-15"
              text="Save"
              type="submit"
              loading={
                statusOfCreateInvoice === "loading" ||
                statusOfEditInvoice === "loading"
              }
            />
          </div>
        </form>
      </Col>

      <DeleteItemModal
        show={deleteModal !== null}
        onCloseClick={() => setDeleteModal(null)}
        onDeleteClick={async () => {
          if (deleteModal) {
            const attachmentId = deleteModal;
            if (quoteDetails) {
              dispatch(
                deleteAttachment({
                  quote_id: quoteDetails.id,
                  attachment_id: attachmentId,
                }),
              );
            } else if (invoiceData) {
              dispatch(
                deleteAttachment({
                  quote_id: invoiceData.quote.id,
                  attachment_id: attachmentId,
                }),
              );
            }
          }
          setDeleteModal(null);
        }}
      />
    </Row>
  );
};

export default ManageInvoiceSummary;
